import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import type { ConsentDocumentDetail } from '@/api/consentDocuments/consentDocumentsApi.types';
import {
  useArchiveConsentDocumentMutation,
  useConsentDocumentQuery,
  useCreateConsentDocumentMutation,
  useUpdateConsentDocumentMutation,
} from '@/hooks/useConsentDocuments';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Icon } from '@/components/common/Icon';
import { FieldError } from '@/components/ui/fieldError';
import { ToggleSwitch } from '@/components/ui/toggleSwitch';
import { ConfirmDialog } from '@/components/ui/confirmDialog';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { RichTextEditor } from '@/components/ui/richTextEditor';
import { richTextHasContent } from '@/utils/richText';
import { isBlank } from '@/utils/formValidation';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { cn } from '@/lib/utils';

interface FormInitialValues {
  titleEn: string;
  titlePl: string;
  bodyEn: string;
  bodyPl: string;
  isRequired: boolean;
  sortOrder: number;
}

interface ConsentDocumentEditorProps {
  documentId: string | null;
  sortOrderForNew: number;
  onClose: () => void;
}

export function ConsentDocumentEditor({
  documentId,
  sortOrderForNew,
  onClose,
}: ConsentDocumentEditorProps) {
  if (documentId === null) {
    const emptyInitial: FormInitialValues = {
      titleEn: '',
      titlePl: '',
      bodyEn: '',
      bodyPl: '',
      isRequired: false,
      sortOrder: sortOrderForNew,
    };
    return <ConsentDocumentForm documentId={null} initial={emptyInitial} onClose={onClose} />;
  }
  return <ExistingConsentDocumentEditor documentId={documentId} onClose={onClose} />;
}

interface ExistingConsentDocumentEditorProps {
  documentId: string;
  onClose: () => void;
}

function ExistingConsentDocumentEditor({
  documentId,
  onClose,
}: ExistingConsentDocumentEditorProps) {
  const detailQuery = useConsentDocumentQuery(documentId, true);

  if (detailQuery.isLoading === true) {
    return (
      <div className="py-16 flex justify-center">
        <LoadingSpinner className="w-7 h-7" />
      </div>
    );
  }

  if (detailQuery.isError === true || detailQuery.data === undefined) {
    return (
      <QueryErrorState
        onRetry={function retry() {
          detailQuery.refetch();
        }}
      />
    );
  }

  return (
    <ConsentDocumentForm
      documentId={documentId}
      initial={toInitialValues(detailQuery.data)}
      onClose={onClose}
    />
  );
}

function toInitialValues(detail: ConsentDocumentDetail): FormInitialValues {
  return {
    titleEn: detail.currentVersion.titleEn,
    titlePl: detail.currentVersion.titlePl,
    bodyEn: detail.currentVersion.bodyEn,
    bodyPl: detail.currentVersion.bodyPl,
    isRequired: detail.isRequired,
    sortOrder: detail.sortOrder,
  };
}

type EditorLanguage = 'en' | 'pl';

interface ConsentDocumentFormProps {
  documentId: string | null;
  initial: FormInitialValues;
  onClose: () => void;
}

function ConsentDocumentForm({ documentId, initial, onClose }: ConsentDocumentFormProps) {
  const { t } = useTranslation(['settings', 'common']);
  const createMutation = useCreateConsentDocumentMutation();
  const updateMutation = useUpdateConsentDocumentMutation();
  const archiveMutation = useArchiveConsentDocumentMutation();

  const [titleEn, setTitleEn] = useState(initial.titleEn);
  const [titlePl, setTitlePl] = useState(initial.titlePl);
  const [bodyEn, setBodyEn] = useState(initial.bodyEn);
  const [bodyPl, setBodyPl] = useState(initial.bodyPl);
  const [isRequired, setIsRequired] = useState(initial.isRequired);
  const [activeLanguage, setActiveLanguage] = useState<EditorLanguage>('en');
  const [showValidation, setShowValidation] = useState(false);
  const [isConfirmingArchive, setIsConfirmingArchive] = useState(false);

  const isEditing = documentId !== null;
  const isSaving = createMutation.isPending || updateMutation.isPending;
  const isInvalid =
    isBlank(titleEn) ||
    isBlank(titlePl) ||
    richTextHasContent(bodyEn) === false ||
    richTextHasContent(bodyPl) === false;

  async function save() {
    if (isInvalid === true) {
      setShowValidation(true);
      showErrorNotification(t('settings:consentDocuments.incomplete'));
      return;
    }
    const payload = {
      titleEn: titleEn.trim(),
      titlePl: titlePl.trim(),
      bodyEn: bodyEn,
      bodyPl: bodyPl,
      isRequired: isRequired,
      sortOrder: initial.sortOrder,
    };
    try {
      if (documentId === null) {
        await createMutation.mutateAsync(payload);
      } else {
        await updateMutation.mutateAsync({ documentId: documentId, payload: payload });
      }
      showSuccessNotification(t('settings:consentDocuments.saved'));
      onClose();
    } catch (saveError) {
      const message = resolveApiErrorMessage(saveError);
      showErrorNotification(message.length > 0 ? message : t('settings:consentDocuments.saveError'));
    }
  }

  function archive() {
    if (documentId === null) {
      return;
    }
    archiveMutation.mutate(documentId, {
      onSuccess: function done() {
        showSuccessNotification(t('settings:consentDocuments.archived'));
        onClose();
      },
      onError: function fail(archiveError) {
        const message = resolveApiErrorMessage(archiveError);
        showErrorNotification(
          message.length > 0 ? message : t('settings:consentDocuments.archiveError')
        );
      },
    });
  }

  return (
    <Card padding="md">
      <div className="flex items-center gap-2 mb-4">
        <Button variant="ghost" size="small" onClick={onClose}>
          <Icon name="ArrowLeft" /> {t('common:actions.back')}
        </Button>
        <h2 className="text-sm font-bold text-zooindigo">
          {isEditing === true
            ? t('settings:consentDocuments.editTitle')
            : t('settings:consentDocuments.newTitle')}
        </h2>
      </div>

      <div className="inline-flex bg-zoooffwhite border border-zooborder rounded-lg p-1 mb-4">
        {(['en', 'pl'] as EditorLanguage[]).map(function renderLanguageTab(language) {
          return (
            <button
              key={language}
              type="button"
              onClick={function select() {
                setActiveLanguage(language);
              }}
              className={cn(
                'px-3 py-1 rounded-md text-xs font-bold transition-colors',
                activeLanguage === language ? 'bg-zooprimary text-zoowhite' : 'text-zoomuted'
              )}
            >
              {language === 'en'
                ? t('settings:consentDocuments.english')
                : t('settings:consentDocuments.polish')}
            </button>
          );
        })}
      </div>

      <div className="space-y-4">
        <div className={cn('space-y-1.5', activeLanguage === 'en' ? '' : 'hidden')}>
          <Label htmlFor="consent-title-en">{t('settings:consentDocuments.titleLabel')}</Label>
          <Input
            id="consent-title-en"
            value={titleEn}
            hasError={showValidation === true && isBlank(titleEn)}
            disabled={isSaving}
            onChange={function update(changeEvent) {
              setTitleEn(changeEvent.target.value);
            }}
          />
          <RichTextEditor
            value={initial.bodyEn}
            onChange={setBodyEn}
            isDisabled={isSaving}
            ariaLabel={t('settings:consentDocuments.contentLabel')}
          />
        </div>

        <div className={cn('space-y-1.5', activeLanguage === 'pl' ? '' : 'hidden')}>
          <Label htmlFor="consent-title-pl">{t('settings:consentDocuments.titleLabel')}</Label>
          <Input
            id="consent-title-pl"
            value={titlePl}
            hasError={showValidation === true && isBlank(titlePl)}
            disabled={isSaving}
            onChange={function update(changeEvent) {
              setTitlePl(changeEvent.target.value);
            }}
          />
          <RichTextEditor
            value={initial.bodyPl}
            onChange={setBodyPl}
            isDisabled={isSaving}
            ariaLabel={t('settings:consentDocuments.contentLabel')}
          />
        </div>

        {showValidation === true && isInvalid === true ? (
          <FieldError messageKey="settings:consentDocuments.incomplete" />
        ) : null}

        <div className="flex items-center justify-between gap-3 pt-2">
          <span className="text-sm font-medium text-zooindigo">
            {t('settings:consentDocuments.requiredLabel')}
          </span>
          <ToggleSwitch
            isChecked={isRequired}
            isDisabled={isSaving}
            accessibleLabel={t('settings:consentDocuments.requiredLabel')}
            onToggle={function toggle() {
              setIsRequired(function invert(previous) {
                return previous === false;
              });
            }}
          />
        </div>

        {isEditing === true ? (
          <p className="text-xs text-zoomuted">{t('settings:consentDocuments.editVersionHint')}</p>
        ) : null}
      </div>

      <div className="flex items-center justify-between mt-6">
        {isEditing === true ? (
          <Button
            type="button"
            variant="linkDanger"
            onClick={function openConfirm() {
              setIsConfirmingArchive(true);
            }}
          >
            <Icon name="Trash" /> {t('settings:consentDocuments.archive')}
          </Button>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-3">
          <Button type="button" variant="outline" disabled={isSaving} onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="button" isLoading={isSaving} onClick={save}>
            {t('settings:consentDocuments.save')}
          </Button>
        </div>
      </div>

      {isConfirmingArchive === true ? (
        <ConfirmDialog
          tone="danger"
          iconName="Trash"
          title={t('settings:consentDocuments.archiveConfirmTitle')}
          description={t('settings:consentDocuments.archiveConfirmBody')}
          confirmLabel={t('settings:consentDocuments.archive')}
          isConfirming={archiveMutation.isPending}
          onConfirm={archive}
          onCancel={function closeConfirm() {
            setIsConfirmingArchive(false);
          }}
        />
      ) : null}
    </Card>
  );
}
